import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { showToast } from 'shared/ui'
import { errorHandler } from 'shared/api'
import type { IAlert } from 'shared/ui/Alert/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { applyCustomSort, mergeReorderedSubset } from 'shared/ui/DragAndDrop'
import { RequestCancelAxios } from 'shared/api/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { logger } from 'shared/lib'
import { type IUser, UsersApi } from 'entities/Users'
import { ContactsApi } from 'entities/Contacts'
import type { Contact } from 'entities/Contacts/model/Contact'
import type { IResponseContact, IResponseCustomField } from 'entities/Contacts/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { complianceCheckUrl } from 'entities/Compliance'
import { PhoneFieldSearchStore } from 'widgets/ContactsInfoForm/ui/PhoneFieldSearch/store/phoneFieldSearchStore'
import { EnumSyncType } from 'widgets/ContactsDetails/store/types'

export enum MainFiledTypes {
  firstName = 'first_name',
  lastName = 'last_name',
  email = 'email',
  number = 'number',
  owner = 'owner',
}

export type IMainFields = {
  first_name: string
  last_name: string
  email: string
  number: string
  owner: IUser | null
}

type IContactInfoFormStoreConfig = {
  onChangeAlert?: (item: IAlert | null) => void
  addNewContactLocallyAfterCreate?: (contact: Contact, params?: { isNew?: boolean }) => void
  isVariantCreateContactModal?: boolean
  onChangeIsFirstEditMode?: (value: boolean) => void
  onSync?: (type: EnumSyncType.contacts_info) => void
  handleUpdateContact?: (contact: IResponseContact) => void
  variantView: string
}

export class ContactInfoFormStore {
  private _contactId: number | null = null
  private _updatingFields = new Map<string, string>()
  private _searchOwner = ''
  private _mainFields: IMainFields = {
    first_name: '',
    last_name: '',
    email: '',
    number: '',
    owner: null,
  }
  private _errors = new Map<string, string>()
  private _customFields = new Map<string, IResponseCustomField>()
  private _initialCustomFields = new Map<string, IResponseCustomField>()
  private _alert: IAlert | null = null
  private _disposeAlert: IReactionDisposer | null = null
  private _disposeOrganizationMembers: IReactionDisposer | null = null
  private _debounceLoadCustomFields: ReturnType<typeof debounce>
  private _config: IContactInfoFormStoreConfig | null = null
  private _isFirstEditMode = false
  private _phoneFieldSearchStore = new PhoneFieldSearchStore()
  private _loadingCustomField = false
  private _cancelRequestLoadCustomFields = new RequestCancelAxios()
  private _cancelRequestLoadCustomFieldsOrder = new RequestCancelAxios()
  private _customFieldsOrder: string[] = []
  private _currentContact: Contact | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._debounceLoadCustomFields = debounce(this._loadCustomFields, 1000)

    this.reactionOrganizationMembers()
    this.reactionAlert()
    this.loadCustomFieldsOrder()
  }

  setConfig = (config: IContactInfoFormStoreConfig) => {
    this._config = config

    this._phoneFieldSearchStore.setConfig({
      addNewContactLocallyAfterCreate: config?.addNewContactLocallyAfterCreate,
    })
  }

  setIsFirstEditMode = (value: boolean) => {
    this._isFirstEditMode = value
    this._config?.onChangeIsFirstEditMode?.(value)
  }

  get loadingCustomField() {
    return this._loadingCustomField
  }

  get config() {
    return this._config
  }

  get isVariantCreateContactModal() {
    return Boolean(this._config?.isVariantCreateContactModal)
  }

  get phoneFieldSearchStore() {
    return this._phoneFieldSearchStore
  }

  get isFirstEditMode() {
    return this._isFirstEditMode
  }

  get contact() {
    if (this._contactId === null) return

    return this._entities.contactsStore.getItem(this._contactId) || this._currentContact
  }

  get updatingFields() {
    return this._updatingFields
  }

  get errors() {
    return this._errors
  }

  get searchOwner() {
    return this._searchOwner
  }

  get mainFields() {
    return this._mainFields
  }

  get number() {
    return this._mainFields.number
  }

  get customFieldsList() {
    return applyCustomSort(
      Array.from(this._customFields.values()),
      this._customFieldsOrder,
      (customField) => customField.field_key,
      { newItemsAt: 'top' }
    )
  }

  get visibleCustomFieldsList() {
    return this.customFieldsList.filter((customField) => customField.visible)
  }

  get membersList() {
    const members = this._entities.usersStore.membersList.filter((member) => !member.isViewOnlyRole)

    if (this._searchOwner) {
      return members.filter((member) =>
        member?.name.toLowerCase().includes(this._searchOwner.toLowerCase())
      )
    }

    return members
  }

  get membersCount() {
    return this.membersList.length
  }

  get hasMembers() {
    return this.membersList.length !== 0
  }

  clearSearchOwner = () => {
    this._searchOwner = ''
  }

  handleSearchOwner = (value: string) => {
    this._searchOwner = value
  }

  hasCustomFields = (isVisibleOnly: boolean) => {
    return isVisibleOnly
      ? this.visibleCustomFieldsList.length !== 0
      : Boolean(this.customFieldsList.length)
  }

  getVisibleCustomFieldsList = (isVisibleOnly: boolean) => {
    return isVisibleOnly ? this.visibleCustomFieldsList : this.customFieldsList
  }

  syncFields = (contactId: number) => {
    this.setContactId(contactId)
    this.refreshContactOwner()
    this.setMainFields()
    this.syncCustomField(contactId)
  }

  refreshContactOwner = async () => {
    if (!this.contact) return
    const owner = this._entities.usersStore.getItem(this.contact.owner_id || undefined)
    if (!owner) return

    runInAction(() => {
      this._mainFields = {
        ...this._mainFields,
        owner: owner,
      }
    })
  }

  syncCustomField = (id: number) => {
    const contact = this._entities.contactsStore.getItem(id)
    if (!contact) return

    contact.customFields.forEach((customField) => {
      const updatedField = this._customFields.get(customField.field_key)
      if (!updatedField) return

      this._customFields.set(updatedField.field_key, customField)
    })
  }

  setMainFields = async () => {
    if (!this.contact) return

    const owner = this._entities.usersStore.getItem(this.contact.owner_id)

    runInAction(() => {
      if (!this.contact) return

      if (this.contact.id === -1) {
        this._mainFields = {
          first_name: '',
          last_name: '',
          number: '',
          email: '',
          owner: null,
        }

        return
      }

      this._mainFields = {
        first_name: this.contact.first_name || '',
        last_name: this.contact.last_name || '',
        number: this.contact.number || '',
        email: this.contact.email || '',
        owner: owner || null,
      }
    })
  }

  getInitialValue = async (field: MainFiledTypes) => {
    if (field === MainFiledTypes.owner) return null

    if (!this.contact) return null

    return this.contact[field]
  }

  loadCustomFieldsOrder = async () => {
    try {
      this._cancelRequestLoadCustomFieldsOrder.init()

      const { data } = await UsersApi.getUsersUiSettings('sort-custom-fields', {
        cancelToken: this._cancelRequestLoadCustomFieldsOrder.token,
      })

      runInAction(() => {
        this._customFieldsOrder = data.data?.items ?? []
      })
    } catch (e) {
      logger.error(e)
    }
  }

  reorderCustomFields = async (orderedKeys: string[]) => {
    try {
      this._cancelRequestLoadCustomFieldsOrder.reset()

      const fullCurrentOrder = this.customFieldsList.map((customField) => customField.field_key)
      const merged = mergeReorderedSubset(fullCurrentOrder, orderedKeys)

      const isSameOrder =
        merged.length === this._customFieldsOrder.length &&
        merged.every((key, index) => key === this._customFieldsOrder[index])

      if (isSameOrder) return

      runInAction(() => {
        this._customFieldsOrder = merged
      })

      await UsersApi.updateUsersUiSettings({
        source_type: 'sort-custom-fields',
        items: merged,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _loadCustomFields = async (id: number) => {
    try {
      runInAction(() => {
        this._loadingCustomField = true
      })

      this._cancelRequestLoadCustomFields.init()

      const response = await ContactsApi.getContactsCustomFields(id, {
        cancelToken: this._cancelRequestLoadCustomFields.token,
      })

      runInAction(() => {
        response?.data?.data?.forEach((field) => {
          this._customFields.set(field.field_key, field)
          this._initialCustomFields.set(field.field_key, field)
        })
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingCustomField = false
      })
    }
  }

  setContactId = (contactId: number, contact?: Contact) => {
    if (isNaN(contactId)) return
    if (this._contactId === contactId) return

    this._contactId = contactId
    this._currentContact = contact ?? null

    this.init(contactId)
  }

  onSync = () => {
    this._config?.onSync?.(EnumSyncType.contacts_info)
  }

  init = async (contactId: number) => {
    this.setMainFields()

    if (contactId > 0) {
      this._loadingCustomField = true
      this._debounceLoadCustomFields(contactId)
    }
  }

  handleSaveOwner = async (id: number, contact: Contact) => {
    if (this._mainFields.owner?.id === id) return

    this._updatingFields.set(MainFiledTypes.owner, MainFiledTypes.owner)

    try {
      const { data: updatedContact } = await ContactsApi.updateContactsByIdFields(contact.id, {
        owner_id: id,
      })

      this._config?.handleUpdateContact?.(updatedContact)
      this.onSync()
      this.refreshContactOwner()
      this.setAlert({
        type: 'infoBlue',
        desc: 'Contact updated',
      })
    } catch (e) {
      const err = e as Error
      const { type, error } = await errorHandler<{ [key: string]: string }>(err)

      if (type === 'axios-error') {
        const data = error?.response?.data || {}
        const errorKey = Object.keys(data)[0]
        const errorData = Array.isArray(data[errorKey]) ? data[errorKey][0] : data[errorKey]

        if (typeof data.status === 'number' && data.status === 404) {
          this.showAccessErrorModal()
        } else if (errorData) {
          this._errors.set(MainFiledTypes.owner, errorData)
          showToast({
            title: errorData,
            type: 'error',
          })
        }
      }
      this.refreshContactOwner()
    } finally {
      this._updatingFields.delete(MainFiledTypes.owner)
    }
  }

  showAccessErrorModal = () => {
    modalStore.addModal({
      id: 'contactDetailsAccessError',
      type: ModalTypeList.FAIL,
      desc: 'You don’t have access to this contact. Contact your Admin to extend access.',
      infoModalTextAction: 'Close',
    })
  }

  handleSaveMainField = async (field: MainFiledTypes, contact: Contact) => {
    if (field === MainFiledTypes.owner) return true

    if (
      contact[field] === this._mainFields[field] ||
      (contact[field] === null && this._mainFields[field] === '')
    ) {
      return true
    }

    if (field === MainFiledTypes.number) {
      const { number } = this._mainFields

      if (!number) {
        this._errors.set(field, 'Phone number is required')

        const initialValue = await this.getInitialValue(field)

        if (initialValue) this.handleUpdateMainField(field, initialValue)

        return false
      }
    }

    this._updatingFields.set(field, field)

    try {
      const { data: updatedContact } = await ContactsApi.updateContactsByIdFields(contact.id, {
        [field]: this._mainFields[field],
      })
      this._entities.contactsStore.addItem(updatedContact)

      this._config?.handleUpdateContact?.(updatedContact)
      this.onSync()
      this.setAlert({
        type: 'infoBlue',
        desc: 'Contact updated',
      })

      return true
    } catch (e) {
      const err = e as Error
      const { type, error } = await errorHandler<{ [key: string]: string | number }>(err)

      if (type === 'axios-error') {
        const data = error?.response?.data || {}
        const errorKey = Object.keys(data)[0]
        const errorData = (() => {
          if (typeof data === 'object') {
            const errors = data[errorKey]

            if (Array.isArray(errors)) return errors.length ? errors[0] : ''

            return errors
          }

          return ''
        })()

        if (data.status === 404) {
          this.showAccessErrorModal()
        } else if (errorData) {
          this._errors.set(field, errorData)
        }
      }

      const initialValue = await this.getInitialValue(field)

      this.handleUpdateMainField(field, initialValue ?? '')

      return false
    } finally {
      this._updatingFields.delete(field)
    }
  }

  handleSaveCustomField = async (field: IResponseCustomField, contact: Contact) => {
    const updatedField = this._customFields.get(field.field_key)
    if (!updatedField) return
    let updatedFieldValue = updatedField.value?.value

    const initialCustomFieldValue = this._initialCustomFields.get(field.field_key)?.value?.value
    if (
      initialCustomFieldValue === updatedFieldValue ||
      (initialCustomFieldValue === null && updatedFieldValue === '')
    ) {
      return
    }

    this._updatingFields.set(field.field_key, field.field_key)

    if ((field.type === 'url' || field.value?.type === 'url') && updatedFieldValue) {
      const complianceValue = await complianceCheckUrl(updatedFieldValue)

      if (complianceValue) updatedFieldValue = complianceValue
    }

    try {
      const { data: updatedContact } = await ContactsApi.updateContactsByIdFields(contact.id, {
        customFields: {
          [field.field_key]: updatedFieldValue || '',
        },
      })

      this._config?.handleUpdateContact?.(updatedContact)
      this.onSync()
      this.setAlert({
        type: 'infoBlue',
        desc: 'Contact updated',
      })

      this._initialCustomFields.set(field.field_key, field)
    } catch (e) {
      const err = e as Error
      const { type, error } = await errorHandler<{
        [key: string]: { [key: string]: string }[] | string | number
      }>(err)

      if (type === 'axios-error') {
        const data = error?.response?.data || {}

        let errorMessage = ''

        Object.keys(data).map((key) => {
          const errors = data[key]
          if (!Array.isArray(errors)) return

          errors.forEach((error) => {
            Object.keys(error).map((key) => {
              errorMessage = `${error[key]}\n`
            })
          })
        })

        if (data.status === 404) this.showAccessErrorModal()

        if (errorMessage) {
          this._errors.set(field.field_key, errorMessage)
        }
      }

      this._loadCustomFields(contact.id)
    } finally {
      this._updatingFields.delete(field.field_key)
    }
  }

  handleUpdateCustomField = (
    field: IResponseCustomField,
    value: string | null,
    resetError?: boolean
  ) => {
    let updatedField = this._customFields.get(field.field_key)
    if (!updatedField) return

    updatedField = {
      ...updatedField,
      value: {
        type: updatedField.value?.type || updatedField?.type || 'text',
        value: value,
      },
    }

    if (resetError) this.resetFiledError(field.field_key)

    this._customFields.set(field.field_key, updatedField)
  }

  handleUpdateMainField = (field: MainFiledTypes, value: string, resetError?: boolean) => {
    if (resetError) this.resetFiledError(field)

    this._phoneFieldSearchStore.resetError()

    if (field !== MainFiledTypes.owner) this._mainFields[field] = value
  }

  setPhone = (value: string) => {
    this.handleUpdateMainField(MainFiledTypes.number, value)
    this._phoneFieldSearchStore.validateNumber(value, 'fast')
  }

  reactionOrganizationMembers = () => {
    this._disposeOrganizationMembers?.()
    this._disposeOrganizationMembers = reaction(
      () => this._entities.usersStore.membersIdsList,
      () => {
        this.refreshContactOwner()
      }
    )
  }

  get alert() {
    return this._alert
  }

  setAlert = (item: IAlert | null) => {
    this._alert = item
    this._config?.onChangeAlert?.(item)
  }

  dispose = () => {
    this._contactId = null
    this._currentContact = null
    this._cancelRequestLoadCustomFields.reset()
    this._cancelRequestLoadCustomFieldsOrder.reset()
    this._debounceLoadCustomFields.cancel()
    this._errors.clear()
    this._customFields.clear()
    this._initialCustomFields.clear()
    this._disposeAlert?.()
    this._disposeOrganizationMembers?.()
    this.resetFields()
    this.resetAlert()
  }

  resetFields = () => {
    this._mainFields = {
      first_name: '',
      last_name: '',
      number: '',
      email: '',
      owner: null,
    }
  }

  resetFiledError = (field: string) => {
    if (this._errors.get(field)) this._errors.delete(field)
  }

  resetAlert = () => {
    this._alert = null
    this._config?.onChangeAlert?.(null)
  }

  reactionAlert = () => {
    this._disposeAlert?.()
    this._disposeAlert = reaction(
      () => this.alert,
      (alert) => {
        if (alert) {
          setTimeout(() => {
            runInAction(() => {
              this.resetAlert()
            })
          }, 5000)
        }
      }
    )
  }

  get variantView() {
    return this._config?.variantView
  }

  get hasCallPopUp() {
    return this.variantView === 'callPopUp'
  }

  get hasContactsDetails() {
    return this.variantView === 'contactDetails'
  }

  get zIndex() {
    return this.hasCallPopUp ? ZIndex.OVERLAY_TOP_1 : undefined
  }

  get blockViewKey() {
    return this.hasCallPopUp ? 'callInsightAllFields' : 'contactDetailsAllFields'
  }
}
